import type { ComponentProps } from "react";
import { MaximizeIcon, MinimizeIcon, XIcon } from "lucide-react";

import { cn } from "./lib/cn";
import { Button } from "./ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";

/** The three window controls a docked conversation panel offers. */
export const DOCK_ACTION = {
  CLOSE: "CLOSE",
  EXPAND: "EXPAND",
  MINIMIZE: "MINIMIZE",
} as const;
export type DockAction = (typeof DOCK_ACTION)[keyof typeof DOCK_ACTION];

const DOCK_ACTION_ICON = {
  [DOCK_ACTION.CLOSE]: XIcon,
  [DOCK_ACTION.EXPAND]: MaximizeIcon,
  [DOCK_ACTION.MINIMIZE]: MinimizeIcon,
} as const;

export type DockProps = ComponentProps<"section">;
/**
 * Viewport-anchored frame that holds the dock panel and its shelf.
 *
 * Pointer events are off on the frame and back on for its interactive children, so the empty
 * region above the shelf never intercepts clicks meant for the page behind it.
 */
export const Dock = ({ className, ...props }: DockProps) => (
  <section
    className={cn("pointer-events-none fixed inset-x-0 bottom-0 z-50", className)}
    data-slot="dock"
    {...props}
  />
);

/**
 * Width at which the panel stops filling the viewport and becomes the corner window.
 *
 * This is Tailwind's `sm` breakpoint, which is what {@link DockPanel} switches on. A host that
 * also decides whether to wrap the panel in a dialog must ask the same question with the same
 * number, or a band of widths gets a full-viewport panel with no dialog behavior around it.
 */
export const DOCK_PANEL_COMPACT_MIN_WIDTH_PX = 640;

/** Matches the viewport widths where {@link DockPanel} fills the screen. */
export const DOCK_PANEL_FULL_WIDTH_MEDIA_QUERY = `(max-width: ${DOCK_PANEL_COMPACT_MIN_WIDTH_PX - 0.02}px)`;

export type DockPanelProps = ComponentProps<"section">;
/**
 * The compact conversation window.
 *
 * Quiet borders define the workspace instead of a raised chat-window shadow: the panel belongs
 * to the page it floats over. It fills the viewport below the `sm` breakpoint, where a corner
 * window would leave no usable transcript width.
 */
export const DockPanel = ({ className, ...props }: DockPanelProps) => (
  <section
    className={cn(
      "pointer-events-auto relative flex h-dvh w-screen flex-col overflow-hidden bg-card backdrop-blur-none",
      "sm:fixed sm:right-4 sm:bottom-[calc(56px+env(safe-area-inset-bottom))] sm:h-[min(680px,calc(100vh-96px))] sm:w-[480px] sm:rounded-lg sm:border",
      className,
    )}
    data-slot="dock-panel"
    {...props}
  />
);

export type DockPanelAnchorProps = ComponentProps<"div">;
/** Positions a dock panel from the shelf item that owns it. */
export const DockPanelAnchor = ({ className, ...props }: DockPanelAnchorProps) => (
  <div
    className={cn("pointer-events-auto relative flex shrink-0 items-end", className)}
    data-slot="dock-panel-anchor"
    {...props}
  />
);

export type DockPanelHeaderProps = ComponentProps<"div">;
/** Trailing window-control row of a {@link DockPanel}. */
export const DockPanelHeader = ({ className, ...props }: DockPanelHeaderProps) => (
  <div
    className={cn("flex shrink-0 items-center justify-end gap-0.5 p-1", className)}
    data-slot="dock-panel-header"
    {...props}
  />
);

export type DockPanelActionProps = Omit<ComponentProps<typeof Button>, "children"> & {
  action: DockAction;
  /** Localized accessible name, also used as the tooltip copy. */
  label: string;
};
/** One window control of a {@link DockPanelHeader}, named by its caller for localization. */
export const DockPanelAction = ({ action, label, className, ...props }: DockPanelActionProps) => {
  const Icon = DOCK_ACTION_ICON[action];
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            aria-label={label}
            className={cn("text-muted-foreground", className)}
            data-action={action}
            data-slot="dock-panel-action"
            size="icon-sm"
            type="button"
            variant="ghost"
            {...props}
          >
            <Icon aria-hidden="true" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>{label}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export type DockShelfProps = ComponentProps<"nav">;
/**
 * Persistent re-entry rail listing recent conversations.
 *
 * Overflow clips on the x axis only: an open {@link DockPanel} is anchored inside the shelf and
 * must extend above it, so clipping the y axis would cut the panel down to the shelf's height.
 */
export const DockShelf = ({ className, ...props }: DockShelfProps) => (
  <nav
    className={cn(
      "pointer-events-none flex min-h-[44px] flex-nowrap items-end justify-end gap-1 overflow-x-clip px-2 pb-[env(safe-area-inset-bottom)]",
      className,
    )}
    data-slot="dock-shelf"
    {...props}
  />
);

export type DockShelfItemProps = ComponentProps<"div">;
/** One grouped conversation tab docked flush to the viewport bottom. */
export const DockShelfItem = ({ className, ...props }: DockShelfItemProps) => (
  <div
    className={cn(
      "pointer-events-auto flex max-w-40 items-stretch overflow-hidden rounded-t-md border border-b-0 bg-card/75 shadow-xs backdrop-blur-md sm:max-w-56 dark:bg-card/70",
      className,
    )}
    data-slot="dock-shelf-item"
    {...props}
  />
);

export type DockShelfButtonProps = ComponentProps<typeof Button>;
/** The title action inside a docked shelf item. */
export const DockShelfButton = ({ className, children, ...props }: DockShelfButtonProps) => (
  <Button
    className={cn(
      "pointer-events-auto h-10 min-w-0 flex-1 justify-start rounded-none border-0 bg-transparent font-normal shadow-none hover:bg-card/90 dark:bg-transparent dark:hover:bg-card/85",
      className,
    )}
    data-slot="dock-shelf-button"
    size="sm"
    type="button"
    variant="ghost"
    {...props}
  >
    <span className="min-w-0 truncate">{children}</span>
  </Button>
);

export type DockShelfCloseButtonProps = Omit<ComponentProps<typeof Button>, "children"> & {
  /** Localized accessible name, also used as the tooltip copy. */
  label: string;
};
/** Removes one conversation tab from the dock. */
export const DockShelfCloseButton = ({ label, className, ...props }: DockShelfCloseButtonProps) => (
  <TooltipProvider>
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          aria-label={label}
          className={cn(
            "h-10 w-8 rounded-none bg-transparent px-0 text-muted-foreground shadow-none hover:bg-card/90 dark:bg-transparent dark:hover:bg-card/85",
            className,
          )}
          data-slot="dock-shelf-close-button"
          size="icon-sm"
          type="button"
          variant="ghost"
          {...props}
        >
          <XIcon aria-hidden="true" />
        </Button>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  </TooltipProvider>
);
